import { colors, typography } from '../styles/theme';
import { CONFIRM_VARIANTS } from './ConfirmDialog/confirmConfig';

/** Mismos tokens que `FeedbackModal`. */
export const APP_MODAL_TEXT = '#FBFBFB';
export const APP_MODAL_DIVIDER = '#888888';

const MODAL_PADDING_X = '20px';

export const appModalPaperSx = {
  width: 'min(628px, 95vw)',
  maxWidth: '628px',
  height: { xs: 'min(680px, 70vh)', sm: 'min(700px, 90vh)' },
  maxHeight: { xs: 'min(680px, 70vh)', sm: 'min(700px, 90vh)' },
  bgcolor: colors.backgroundSurface,
  backgroundImage: 'none',
  color: APP_MODAL_TEXT,
  borderRadius: '12px',
  boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: typography.fontFamily.primary,
};

export const appModalTitleBarSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 1.5,
  px: MODAL_PADDING_X,
  pt: '20px',
  pb: 0,
  flexShrink: 0,
};

export const appModalTitleSx = {
  m: 0,
  fontSize: '22px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
  color: APP_MODAL_TEXT,
  fontFamily: typography.fontFamily.primary,
};

export const appModalCloseButtonSx = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  color: APP_MODAL_TEXT,
  bgcolor: colors.backgroundSurface,
  flexShrink: 0,
  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: APP_MODAL_TEXT },
  '&.Mui-disabled': { color: colors.disabledOnDarkText },
};

export const appModalHeaderDividerSx = {
  mt: 2,
  borderColor: APP_MODAL_DIVIDER,
  flexShrink: 0,
};

export const appModalSubtitleSx = {
  mt: 2,
  px: MODAL_PADDING_X,
  mb: 0,
  color: APP_MODAL_TEXT,
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: 1.4,
  flexShrink: 0,
};

export const appModalContentSx = {
  mt: 2,
  px: MODAL_PADDING_X,
  pt: 0,
  pb: 0,
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

export const appModalScrollSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

export const appModalActionsSx = {
  mt: '14px',
  px: MODAL_PADDING_X,
  pt: '14px',
  pb: '16px',
  borderTop: '1px solid rgba(255,255,255,0.08)',
  gap: '10px',
  flexShrink: 0,
};

export const appModalPrimaryButtonSx = {
  textTransform: 'none',
  borderRadius: '8px',
  border: 'none',
  bgcolor: colors.primary,
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '14px',
  px: 2,
  height: 40,
  minWidth: 156,
  boxShadow: 'none',
  '&:hover': { bgcolor: colors.primaryHover, boxShadow: 'none' },
  // Deshabilitado (no se puede): gris y legible. Mientras corre el pedido usar `appModalPrimaryLoadingSx`.
  '&.Mui-disabled': { bgcolor: colors.disabledOnDarkBg, color: colors.disabledOnDarkText },
};

/**
 * Primario con el pedido en curso (`disabled` + spinner): sigue en el verde de Terrio, un poco más
 * apagado, para que se lea que está trabajando y no que no se puede.
 */
export const appModalPrimaryLoadingSx = {
  '&.Mui-disabled': { bgcolor: colors.primary, color: '#ffffff', opacity: 0.72 },
};

/**
 * Confirmación final destructiva (el «Eliminar» que borra de verdad): el mismo rojo que la variante
 * `danger` de `ConfirmDialog` (borrar una lista, un lote, un adjunto). Solo ahí; los pasos previos
 * («Continuar») van en el primario.
 */
export const appModalDangerButtonSx = {
  ...appModalPrimaryButtonSx,
  bgcolor: CONFIRM_VARIANTS.danger.confirmBg,
  '&:hover': { bgcolor: CONFIRM_VARIANTS.danger.confirmBgHover, boxShadow: 'none' },
};

export const appModalDangerLoadingSx = {
  '&.Mui-disabled': { bgcolor: CONFIRM_VARIANTS.danger.confirmBg, color: '#ffffff', opacity: 0.72 },
};

export const appModalSecondaryButtonSx = {
  textTransform: 'none',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '14px',
  height: 40,
  px: 2,
  color: APP_MODAL_TEXT,
  borderColor: APP_MODAL_DIVIDER,
  '&:hover': {
    borderColor: APP_MODAL_DIVIDER,
    bgcolor: 'rgba(255,255,255,0.04)',
  },
  // Deshabilitado (p. ej. «Cancelar» mientras corre el pedido): gris y legible, nunca negro.
  '&.Mui-disabled': { color: colors.disabledOnDarkText, borderColor: colors.disabledOnDarkBorder },
};

export const appModalFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    minHeight: 40,
    borderRadius: '8px',
    bgcolor: colors.backgroundSurface,
    color: APP_MODAL_TEXT,
    fontSize: '14px',
    fontFamily: typography.fontFamily.primary,
    '& fieldset': { borderColor: APP_MODAL_DIVIDER },
    '&:hover fieldset': { borderColor: APP_MODAL_DIVIDER },
    '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: '1px' },
  },
  // Notch del label: MUI mide el legend en `0.75em` de la letra del input (acá 14px → 10,5px) y el
  // label achicado mide 0.75 × 1rem (12px): con labels largos el borde cruzaba el texto
  // («Escribí ELIMINAR para confirmar»). Mismo tamaño que el label → el corte queda detrás.
  '& .MuiOutlinedInput-notchedOutline legend': { fontSize: '0.75rem' },
  '& .MuiOutlinedInput-input': {
    py: '10px',
    color: APP_MODAL_TEXT,
    fontSize: '14px',
    '&::placeholder': { color: colors.textSecondary, opacity: 1 },
  },
  '& .MuiSelect-select': {
    py: '10px',
    px: '12px',
  },
  '& .MuiSelect-icon': { color: colors.textSecondary },
};
